import { useCallback, useEffect, useRef } from "react";
import { useAuth } from "../context/UserContextProvider";
import {
  queryMarketItemPurchaseDTOListByParams,
  queryMascotInventoryItemDTOListByParams,
} from "../requests/GamificationRequests";
import {
  applyMascotSlotChangeList,
  planMascotSlotChangeList,
} from "../utilization/MascotInventoryUtilization";

// Global "profile banner" applied to EVERY page hero. The source of truth is the SERVER: the BANNER
// item in the learner's mascot inventory (the same equip table as hats and glasses). The profile
// background picker creates / swaps / removes that item; every hero reads it from the user context's
// `mascotInventoryItemDTOList`. So a background chosen on mobile shows on the web and vice versa,
// and a friend viewing the profile sees the same one (ViewUserProfileScreen reads that item too).
//
// It used to live only in this browser's localStorage, so web and mobile never saw each other's
// choice. That legacy value is moved to the server once (useLegacyHeroBannerMigration) and dropped.
const LEGACY_KEY = "cu_hero_banner_img";
const BANNER_SLOT = ["BANNER"];

// The banner value ends up inside a CSS `url(...)`. Only ever accept app-owned asset paths
// (e.g. /market/banner/koi-fish-banner.webp); anything else is treated as "no banner".
const SAFE_BANNER_PATH = /^\/[A-Za-z0-9._/-]+$/;
function safeBannerImg(img: string): string {
  return img && SAFE_BANNER_PATH.test(img) ? img : "";
}

/** The asset path of a BANNER market item. */
export function bannerImgOf(marketItemName: string): string {
  return safeBannerImg(marketItemName ? `/market/banner/${marketItemName}.webp` : "");
}

const findEquippedBanner = (inventoryItemList: any[]) =>
  (inventoryItemList || []).find((item: any) => item?.marketItemDTO?.type === "BANNER") || null;

// Left-weighted dark-teal scrim over the chosen banner — the DS canon
// (banner-bg.js): heavy on the text side, fading to 0.2 by 72% so the photo
// stays vivid on the right (the old full-width 0.82→0.34 scrim read as a
// flat gray tint over the whole hero).
export function heroBannerBackground(img: string): string {
  const safe = safeBannerImg(img);
  // no valid banner → empty string, so the hero keeps its default teal gradient
  if (!safe) return "";
  const scrim =
    "linear-gradient(100deg, rgba(15, 72, 89, 0.78) 0%, rgba(15, 72, 89, 0.5) 40%, rgba(15, 72, 89, 0.2) 72%)";
  return `${scrim}, url("${safe}") center / cover no-repeat`;
}

/** The equipped banner's asset path, or "" for the default hero. */
export function useHeroBanner(): string {
  const { mascotInventoryItemDTOList } = useAuth();
  return bannerImgOf(findEquippedBanner(mascotInventoryItemDTOList)?.marketItemDTO?.name || "");
}

/**
 * Writes the profile background to the server and updates the context inventory. `marketItem` is
 * an OWNED banner market item, or null for the default hero. Resolves true when it was stored.
 */
export function useSetHeroBanner() {
  const { userInformation, userProfile, mascotInventoryItemDTOList, setMascotInventoryItemDTOList } =
    useAuth();
  return useCallback(
    async (marketItem: any | null): Promise<boolean> => {
      if (!userInformation?.id || !userProfile?.id) return false;
      const saved = mascotInventoryItemDTOList || [];
      const changeList = planMascotSlotChangeList(
        saved,
        marketItem ? [{ id: marketItem.id, type: "BANNER" }] : [],
        BANNER_SLOT
      );
      if (changeList.length === 0) return true;
      const { inventoryItemList, failedTypeList } = await applyMascotSlotChangeList(
        saved,
        changeList,
        userInformation.id,
        userProfile.id
      );
      setMascotInventoryItemDTOList(inventoryItemList);
      return failedTypeList.length === 0;
    },
    [userInformation?.id, userProfile?.id, mascotInventoryItemDTOList, setMascotInventoryItemDTOList]
  );
}

function readLegacyBannerImg(): string {
  try {
    return safeBannerImg(localStorage.getItem(LEGACY_KEY) || "");
  } catch (e) {
    return "";
  }
}

function dropLegacyBannerImg() {
  try {
    localStorage.removeItem(LEGACY_KEY);
  } catch (e) {}
}

/**
 * One-time move: when this browser still holds a legacy (local-only) background and the server has
 * NO banner equipped, equip it on the server. The local value is then dropped — the server is the
 * only source from here on. A banner already on the server wins; a banner the learner no longer owns
 * is not moved. On a network error the local value is kept so the next session retries.
 *
 * It asks the server directly instead of reading the context list, so a list that has not loaded
 * yet is never mistaken for "no banner" (which would equip a second BANNER row).
 */
export function useLegacyHeroBannerMigration() {
  const { userInformation, userProfile, setMascotInventoryItemDTOList } = useAuth();
  const userId = userInformation?.id;
  const userProfileId = userProfile?.id;
  const doneFor = useRef<string | null>(null);
  const setInventoryRef = useRef(setMascotInventoryItemDTOList);
  setInventoryRef.current = setMascotInventoryItemDTOList;

  useEffect(() => {
    if (!userId || !userProfileId) return;
    const runKey = `${userId}:${userProfileId}`;
    if (doneFor.current === runKey) return;
    doneFor.current = runKey;

    const legacyImg = readLegacyBannerImg();
    if (!legacyImg) return;
    (async () => {
      const [inventoryRes, purchaseRes] = await Promise.all([
        queryMascotInventoryItemDTOListByParams({ userId, userProfileId }),
        queryMarketItemPurchaseDTOListByParams({ userId, userProfileId }),
      ]);
      if (inventoryRes.err || purchaseRes.err) return;
      const inventory = (inventoryRes.data as any[]) || [];
      const ownedBanner = ((purchaseRes.data as any[]) || []).find(
        (purchase: any) =>
          purchase?.marketItemDTO?.type === "BANNER" &&
          bannerImgOf(purchase.marketItemDTO.name) === legacyImg
      )?.marketItemDTO;
      if (findEquippedBanner(inventory) || !ownedBanner) {
        dropLegacyBannerImg();
        return;
      }
      const { inventoryItemList, failedTypeList } = await applyMascotSlotChangeList(
        inventory,
        planMascotSlotChangeList(inventory, [{ id: ownedBanner.id, type: "BANNER" }], BANNER_SLOT),
        userId,
        userProfileId
      );
      if (failedTypeList.length > 0) return;
      dropLegacyBannerImg();
      setInventoryRef.current(inventoryItemList);
    })().catch(() => {});
  }, [userId, userProfileId]);
}
